/**
 * i18n middleware and context for React Router v7
 *
 * Uses the new middleware API to initialize i18n instance once per request,
 * making it available to all routes via context.get(i18nContext).
 *
 * Source: https://github.com/troph-team/pixai/blob/main/packages/pixai-server/src/common/i18n/middleware.ts
 */
import type { RouterContextProvider } from 'react-router'
import { createContext, type MiddlewareFunction } from 'react-router'
import type { SupportedLocaleCodes } from './constants'
import { DEFAULT_LOCALE } from './constants'
import i18n from './i18n.base'
import { getLocaleFromPathname } from './utils'

// ============================================================================
// i18n Context Type
// ============================================================================

export interface I18nContext {
  /** Translate a key with optional interpolation options */
  t: (key: string, options?: Record<string, unknown>) => string
  /** Check if a translation key exists */
  exists: (key: string) => boolean
  /** The resolved locale code */
  locale: SupportedLocaleCodes
}

// Default i18n context value (used before middleware runs)
const defaultI18nContext: I18nContext = {
  t: (key: string) => key,
  exists: () => false,
  locale: DEFAULT_LOCALE,
}

// ============================================================================
// Context Creation
// ============================================================================

/**
 * React Router context for i18n.
 * Use with getI18n(context) in loaders to get the i18n instance.
 */
export const i18nContext = createContext<I18nContext>(defaultI18nContext)

/**
 * @deprecated @TODO Should remove it later, just use loaderArgs.context.get().
 */
export function getI18n(context: Readonly<RouterContextProvider>): I18nContext {
  return context.get(i18nContext)
}

// ============================================================================
// Middleware
// ============================================================================

/**
 * i18n middleware that initializes the translator and sets it to context.
 *
 * Usage in root route:
 *   export const middleware = [i18nMiddleware]
 *
 * Then in loaders:
 *   export function loader({ context }) {
 *     const { t } = getI18n(context)
 *     return { title: t('server:meta.home.title') }
 *   }
 */
export const i18nMiddleware: MiddlewareFunction = async (
  { request, context },
  next,
) => {
  const url = new URL(request.url)
  const locale = getLocaleFromPathname(url.pathname) ?? DEFAULT_LOCALE

  // Clone base instance and change language
  const instance = i18n.cloneInstance()
  await instance.changeLanguage(locale)

  const i18nValue: I18nContext = {
    t: (key: string, options?: Record<string, unknown>): string =>
      instance.t(key, options),
    exists: (key: string): boolean => instance.exists(key),
    locale: (instance.resolvedLanguage ??
      instance.language ??
      DEFAULT_LOCALE) as SupportedLocaleCodes,
  }

  context.set(i18nContext, i18nValue)
  return next()
}
